import { Params, Request } from './Request'
import {
  BranchOffice,
  ClientDetail,
  Company,
  CompanyAgreementSettings,
  CompanyAttributes,
  CompanyEvaluation,
  CompanyRepresentative,
  CsvImportSetting,
  CsvImportSettingGetResponse,
  DetailPropConfig,
  TransactionPropConfig,
  VisitorRegistrationCompanyFields,
  VisitorRegistrationForm,
  VisitorRegistrationFormSubcontractor,
  VisitorRegistrationFormSubcontractorFields,
  VisitorRegistrationFormWorker,
  VisitorRegistrationFormWorkerFields,
  VisitorRegistrationRecord,
  VisitorRegistrationRecordSet,
} from '../types/Company'
import { ApprovalFlow } from '../types/ApprovalFlow'
import { MembershipFee2 } from '../types/Moneyspace'
import { ProjectDetail } from '../types/construction/project'

type LoadCompanyResponse = Company

type LoadClientsResponse = {
  clients: Company[]
}

export default class CompanyRepository {
  private request: Request

  constructor(request: Request) {
    this.request = request
  }

  async loadCompany(companyId: string, silent?: boolean) {
    const response = await this.request.get<LoadCompanyResponse>(
      `/companies/${companyId}`,
      undefined,
      undefined,
      silent
    )
    return response.data
  }

  async saveCompany(id: string, company: CompanyAttributes) {
    const response = await this.request.put<Company>(`/companies/${id}`, {
      name: company.name,
      representativeName: company.representativeName,
      representativeRole: company.representativeRole,
      zipCode: company.zipCode,
      address: company.address,
      phoneNumber: company.phoneNumber,
      phoneNumberMobile: company.phoneNumberMobile,
      bankName: company.bankName,
      bankCode: company.bankCode,
      branchName: company.branchName,
      branchCode: company.branchCode,
      accountType: company.accountType,
      accountNumber: company.accountNumber,
      accountOwner: company.accountOwner,
      payAfterHolidays: company.payAfterHolidays,
      invoiceLicenseNumber: company.invoiceLicenseNumber,
      fiscalPeriod: company.fiscalPeriod,
    })
    return response.data
  }

  async saveTransactionConfig(
    id: string,
    detailPropConfig: DetailPropConfig,
    taxRoundingType: number,
    transactionPropConfig: TransactionPropConfig
  ) {
    const response = await this.request.put<Company>(`/companies/${id}`, {
      detailPropConfig,
      taxRoundingType,
      transactionPropConfig,
    })
    return response.data
  }

  async saveInvoice(id: string, invoiceLessActionType: number) {
    const response = await this.request.put<Company>(`/companies/${id}`, { invoiceLessActionType })
    return response.data
  }

  async saveCovenant(id: string, covenant: string | null) {
    await this.request.put<Company>(`/companies/${id}`, { covenant })
  }

  async loadClients(companyId: string, silent?: boolean) {
    const response = await this.request.get<LoadClientsResponse>(
      `/companies/${companyId}/clients`,
      undefined,
      undefined,
      silent
    )
    return response.data.clients
  }

  async loadClientDetails(companyId: string, silent?: boolean) {
    const response = await this.request.get<{ clients: ClientDetail[] }>(
      `/companies/${companyId}/client-details`,
      undefined,
      undefined,
      silent
    )
    return response.data.clients
  }

  async createClient(companyId: string, inviteCompanyUid: string) {
    await this.request.post(`/companies/${companyId}/clients`, {
      companyUid: inviteCompanyUid,
    })
  }

  async saveClient(companyId: string, clientId: string, client: CompanyAttributes) {
    const response = await this.request.put<Company>(`/companies/${companyId}/clients/${clientId}`, {
      name: client.name,
      representativeName: client.representativeName,
      representativeRole: client.representativeRole,
      zipCode: client.zipCode,
      address: client.address,
      bankName: client.bankName,
      bankCode: client.bankCode,
      branchName: client.branchName,
      branchCode: client.branchCode,
      accountType: client.accountType,
      accountNumber: client.accountNumber,
      accountOwner: client.accountOwner,
    })
    return response.data
  }

  async deleteClient(companyId: string, client: Company) {
    await this.request.delete(`/companies/${companyId}/clients/${client.id}`)
  }

  async acceptClient(companyId: string, clientId: string) {
    await this.request.post(`/companies/${companyId}/clients/${clientId}/accept`)
  }

  async loadApprovalFlows(companyId: string) {
    const response = await this.request.get<{ approvalFlows: ApprovalFlow[] }>(`/companies/${companyId}/approval-flows`)
    return response.data.approvalFlows
  }

  async createApprovalFlow(companyId: string, name: string, users: (string | null)[]) {
    const response = await this.request.post<ApprovalFlow>(`/companies/${companyId}/approval-flows`, {
      name,
      members: users.map((id, index) => ({
        userId: id,
        step: index + 1,
      })),
    })
    return response.data
  }

  async saveApprovalFlow(companyId: string, flowId: string, name: string, users: (string | null)[]) {
    const response = await this.request.put<ApprovalFlow>(`/companies/${companyId}/approval-flows/${flowId}`, {
      name,
      members: users.map((id, index) => ({
        userId: id,
        step: index + 1,
      })),
    })
    return response.data
  }

  async saveAutoReviewBillingPhase(companyId: string, flowId: string, autoReviewBillingPhase: boolean) {
    const response = await this.request.put<ApprovalFlow>(`/companies/${companyId}/approval-flows/${flowId}`, {
      autoReviewBillingPhase,
    })
    return response.data
  }

  async deleteApprovalFlow(companyId: string, flowId: string) {
    await this.request.delete(`/companies/${companyId}/approval-flows/${flowId}`)
  }

  async inviteToken(companyId: string, email?: string) {
    const response = await this.request.post<{ token: string }>(`/companies/${companyId}/invite-client`, { email })
    return response.data.token
  }

  async searchInvoiceLicense(invoiceLicenseNumber: string) {
    const response = await this.request.post<{ name: string | null }>(
      `/validate-invoice-license`,
      { invoiceLicenseNumber },
      true
    )
    return response.data.name
  }

  async saveMembershipFee(companyId: string, membershipFee: MembershipFee2) {
    const chart: (number | null)[][] = []
    let fromAmount = 0
    membershipFee?.conditions.forEach((condition, index) => {
      const toAmount = index === membershipFee.conditions.length - 1 ? null : condition.amount
      chart.push([fromAmount, toAmount, condition.value])
      fromAmount = condition.amount + 1
    })
    await this.request.post(`/companies/${companyId}/membership-fee`, {
      isTaxIn: membershipFee?.taxIn === 2,
      useRate: membershipFee?.type === 2,
      chart,
      maximumFee: membershipFee?.maximumFee,
      roundingType: membershipFee?.roundingType,
    })
  }

  async saveAggregateTransaction(
    companyId: string,
    aggregateTransactions: boolean,
    fixMonth: number | null,
    fixDay: number | null
  ) {
    await this.request.put(`/companies/${companyId}`, {
      aggregateTransactions,
      fixMonth,
      fixDay,
    })
  }

  async validateCompanyUid(uid: string) {
    const response = await this.request.post<{ isUsed: boolean }>('/validate-company-uid', { uid }, true)
    return !response.data.isUsed
  }

  async loadCsvImportSetting(companyId: string): Promise<CsvImportSetting | null> {
    const response = await this.request.get<CsvImportSettingGetResponse | null>(
      `/companies/${companyId}/import-config`,
      undefined,
      {
        forbidden: () => null,
      }
    )
    if (response.data === null) {
      return null
    }
    const { data } = response
    return {
      ...data,
      skippingTargetValues: (data.skippingTargetValues ?? []).map((v) => ({ value: v })),
    }
  }

  async saveCsvImportSetting(companyId: string, setting: CsvImportSetting) {
    await this.request.put(`/companies/${companyId}/import-config`, {
      ...setting,
      skippingTargetValues: setting.skippingTargetValues.map((v) => v.value.trim()),
    })
  }

  async saveWorkAreas(companyId: string, areaIds: string[]) {
    await this.request.put(`/companies/${companyId}/work-areas`, {
      areaIds,
    })
  }

  async saveConstructions(companyId: string, constructions: { typeId: string; detail?: string }[]) {
    await this.request.put(`/companies/${companyId}/constructions`, {
      constructions,
    })
  }

  async downloadClientCsv(filename: string, companyId: string, searchParamName?: string, searchParamInvoice?: number) {
    let name = searchParamName?.trim()
    if (name?.length === 0) {
      name = undefined
    }
    await this.request.download(filename, `/companies/${companyId}/clients/csv`, {
      name,
      invoice: searchParamInvoice,
    })
  }

  async saveClientEvaluation(
    companyId: string,
    clientCompanyId: string,
    communicationSpeedPoint: number,
    irregularitiesResponsePoint: number,
    budgetPoint: number,
    constructionSchedulePoint: number,
    technologyPoint: number,
    comment: string | null
  ) {
    await this.request.post(`/companies/${companyId}/clients/${clientCompanyId}/evaluations`, {
      communicationSpeedPoint,
      irregularitiesResponsePoint,
      budgetPoint,
      constructionSchedulePoint,
      technologyPoint,
      comment,
    })
  }

  async loadClientEvaluation(companyId: string, clientCompanyId: string, silent: boolean) {
    const response = await this.request.get<CompanyEvaluation>(
      `/companies/${companyId}/clients/${clientCompanyId}/evaluations`,
      undefined,
      undefined,
      silent
    )
    return response.data
  }

  async saveClientEvaluationComment(companyId: string, clientCompanyId: string, commentId: string, comment: string) {
    await this.request.put(
      `/companies/${companyId}/clients/${clientCompanyId}/evaluation-comments?comment-id=${commentId}`,
      {
        comment,
      }
    )
  }

  async deleteClientEvaluationComment(companyId: string, clientCompanyId: string, commentId: string) {
    await this.request.delete(
      `/companies/${companyId}/clients/${clientCompanyId}/evaluation-comments?comment-id=${commentId}`
    )
  }

  async postRepresentative(companyId: string, userId: string, message?: boolean) {
    await this.request.post(`/companies/${companyId}/representative`, { userId, message })
  }

  async uploadRepresentativeCertification(companyId: string, files: File[]) {
    const formData = new FormData()
    files.forEach((file, index) => {
      formData.append(`file_${index}`, file)
    })
    await this.request.post(`/companies/${companyId}/representative/certification`, formData)
  }

  async loadRepresentatives() {
    const response = await this.request.get<{ representatives: CompanyRepresentative[] }>(`/company-representatives`)
    return response.data.representatives
  }

  async loadRepresentative(id: string) {
    const response = await this.request.get<CompanyRepresentative>(`/company-representatives/${id}`)
    return response.data
  }

  async acceptRepresentative(companyId: string) {
    await this.request.put(`/companies/${companyId}/representative/accept`)
  }

  async rejectRepresentative(companyId: string, reason: string) {
    await this.request.put(`/companies/${companyId}/representative/reject`, { reject_reason: reason })
  }

  async loadBranchOffices(companyId: string) {
    const response = await this.request.get<{ branchOffices: BranchOffice[] }>(`/companies/${companyId}/branch-offices`)
    return response.data.branchOffices
  }

  async createBranchOffice(companyId: string, name: string) {
    const response = await this.request.post<BranchOffice>(`/companies/${companyId}/branch-offices`, { name })
    return response.data
  }

  async updateBranchOffice(companyId: string, branchOfficeId: string, name: string) {
    const response = await this.request.put<BranchOffice>(`/companies/${companyId}/branch-offices/${branchOfficeId}`, {
      name,
    })
    return response.data
  }

  async deleteBranchOffice(companyId: string, branchOfficeId: string) {
    await this.request.delete(`/companies/${companyId}/branch-offices/${branchOfficeId}`)
  }

  async addBranchOfficeMember(companyId: string, branchOfficeId: string, userId: string) {
    await this.request.post(`/companies/${companyId}/branch-offices/${branchOfficeId}/members`, { userId })
  }

  async removeBranchOfficeMember(companyId: string, branchOfficeId: string, userId: string) {
    await this.request.delete(`/companies/${companyId}/branch-offices/${branchOfficeId}/members`, { userId })
  }

  async loadProjectDetail(companyId: string, projectId: string) {
    const response = await this.request.get<ProjectDetail>(`/companies/${companyId}/projects/${projectId}`)
    return response.data
  }

  async loadVisitorRegistrationForm(companyId: string) {
    const response = await this.request.get<VisitorRegistrationForm | undefined>(
      `/companies/${companyId}/visitor-registration-form`,
      undefined,
      {
        notFound: () => undefined,
      }
    )
    return response.data
  }

  async saveVisitorRegistrationForm(companyId: string, company: VisitorRegistrationCompanyFields) {
    const response = await this.request.post<VisitorRegistrationForm>(
      `/companies/${companyId}/visitor-registration-form`,
      company
    )
    return response.data
  }

  async saveVisitorRegistrationAnswers(companyId: string, answers: Params) {
    const response = await this.request.post<VisitorRegistrationForm>(
      `/companies/${companyId}/visitor-registration-form/answers`,
      answers
    )
    return response.data
  }

  async loadVisitorRegistrationFormWorkers(companyId: string) {
    const response = await this.request.get<VisitorRegistrationFormWorker[]>(
      `/companies/${companyId}/visitor-registration-form/workers`,
      undefined,
      {
        notFound: () => [],
      }
    )
    return response.data
  }

  async createVisitorRegistrationFormWorker(companyId: string, fields: VisitorRegistrationFormWorkerFields) {
    const response = await this.request.post<VisitorRegistrationFormWorker>(
      `/companies/${companyId}/visitor-registration-form/workers`,
      fields
    )
    return response.data
  }

  async updateVisitorRegistrationFormWorker(
    companyId: string,
    workerId: string,
    fields: VisitorRegistrationFormWorkerFields
  ) {
    const response = await this.request.put<VisitorRegistrationFormWorker>(
      `/companies/${companyId}/visitor-registration-form/workers/${workerId}`,
      fields
    )
    return response.data
  }

  async deleteVisitorRegistrationFormWorker(companyId: string, workerId: string) {
    await this.request.delete(`/companies/${companyId}/visitor-registration-form/workers/${workerId}`)
  }

  async loadVisitorRegistrationFormSubcontractors(companyId: string) {
    const response = await this.request.get<VisitorRegistrationFormSubcontractor[]>(
      `/companies/${companyId}/visitor-registration-form/subcontractors`,
      undefined,
      {
        notFound: () => [],
      }
    )
    return response.data
  }

  async createVisitorRegistrationFormSubcontractor(
    companyId: string,
    fields: VisitorRegistrationFormSubcontractorFields
  ) {
    const response = await this.request.post<VisitorRegistrationFormSubcontractor>(
      `/companies/${companyId}/visitor-registration-form/subcontractors`,
      fields
    )
    return response.data
  }

  async updateVisitorRegistrationFormSubcontractor(
    companyId: string,
    subcontractorId: string,
    fields: VisitorRegistrationFormSubcontractorFields
  ) {
    const response = await this.request.put<VisitorRegistrationFormSubcontractor>(
      `/companies/${companyId}/visitor-registration-form/subcontractors/${subcontractorId}`,
      fields
    )
    return response.data
  }

  async deleteVisitorRegistrationFormSubcontractor(companyId: string, subcontractorId: string) {
    await this.request.delete(`/companies/${companyId}/visitor-registration-form/subcontractors/${subcontractorId}`)
  }

  async loadVisitorRegistrationRecords(companyId: string) {
    const response = await this.request.get<VisitorRegistrationRecordSet>(
      `/companies/${companyId}/visitor-registration-records`
    )
    return response.data
  }

  async createVisitorRegistrationRecord(
    companyId: string,
    clientId: string,
    workerIds: string[],
    subcontractorIds: string[]
  ) {
    const response = await this.request.post<VisitorRegistrationRecord>(
      `/companies/${companyId}/visitor-registration-records`,
      { clientId, workerIds, subcontractorIds }
    )
    return response.data
  }

  async loadAgreementSettings(companyId: string) {
    const response = await this.request.get<CompanyAgreementSettings>(`/companies/${companyId}/agreement-settings`)
    return response.data
  }

  async updateAgreementSettings(companyId: string, userIds: string[]) {
    const response = await this.request.post<CompanyAgreementSettings>(`/companies/${companyId}/agreement-settings`, {
      userIds,
    })
    return response.data
  }
}
